import { forwardRef } from 'react';

import { ReactComponent as InfoIcon } from 'assets/icons/1xbet/info-icon.svg';
import { twMergeClsx } from 'utils/tw-with-clsx';
import { InputType, type InputTypePropTypes, type InputRefType } from 'views/shared/input-type';
import { Tooltip } from 'views/shared/tooltip';

const inputStyles =
  'transition-colors md:rounded-none md:bg-[transparent] autofill:bg-base-yellow md:border-b text-dark-gray-200 w-full md:h-9 border-base-gray-500 outline-none md:text-lg md:leading-7 md:px-0 md:pb-2 hover:border-base-dark-gray-200 disabled:border-base-gray-500 focus:border-base-dark-gray-200 placeholder:text-base-gray-600/50';
const inputErrorStyles =
  'border-base-red-100 hover:border-base-red-100 focus:border-base-red-100 text-base-red-100';
const inputMobileStyles =
  'bg-base-gray-700 rounded-xsmall h-10 text-sm font-medium leading-6 px-3 w-full outline-none';
const inputAmountStyles = 'font-medium md:font-bold text-base md:text-2xl leading-6 md:leading-7';

interface InputPropTypes {
  error?: boolean | string | null;
  label?: string;
  upperLabel?: boolean;
  placeholder?: string;
  required?: boolean;
  containerClassName?: string;
  info?: string;
  isMobile?: boolean;
  LeftIcon?: JSX.Element | null;
}

const Input = forwardRef<InputRefType, InputPropTypes & InputTypePropTypes>((props, ref) => {
  const {
    inputClassName,
    amount,
    error,
    containerClassName,
    upperLabel,
    label,
    name,
    id,
    required,
    value,
    info,
    isMobile,
    LeftIcon,
  } = props;

  const inputClassess = twMergeClsx(
    isMobile ? inputMobileStyles : inputStyles,
    inputClassName,
    amount && inputAmountStyles,
    error && inputErrorStyles,
    LeftIcon && 'pl-[46px]',
  );

  return (
    <div className={twMergeClsx('text-base-gray-600 relative', containerClassName)}>
      {label ? (
        <label
          className={twMergeClsx(
            'text-xs md:text-[10px] leading-3 font-normal mb-2 gap-1 flex items-center text-base-gray-800 font-nunito whitespace-nowrap',
            upperLabel && 'uppercase font-semibold tracking-[2px] mb-1',
          )}
          htmlFor={name || id}
        >
          {label}
          {required && <span className="text-base-red-100 opacity-50">*</span>}
          {info && (
            <div className="w-3 h-3">
              <Tooltip
                text={info}
                className="bg-white rounded-xsmall px-3 py-[6px] shadow whitespace-pre mb-2"
              >
                <InfoIcon className="w-3 h-3" />
              </Tooltip>
            </div>
          )}
        </label>
      ) : null}
      <div className="relative">
        {LeftIcon && (
          <div className="absolute left-3 inset-y-0 flex items-center justify-center">
            {LeftIcon}
          </div>
        )}
        <InputType ref={ref} {...props} inputClassName={inputClassess} />
      </div>

      {typeof value === 'string' && error && typeof error === 'string' && (
        <div className="text-base-red-100 text-[10px] leading-3 lowercase whitespace-nowrap absolute -bottom-3 md:-bottom-4">
          {error}
        </div>
      )}
    </div>
  );
});

export { Input };
